import React, { useState, useEffect } from 'react';
import { useGetAnneeActiveQuery, useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import { ClipboardList, Table2, BarChart3, Zap, Wrench, BookOpen, RefreshCw } from 'lucide-react';
import { AlertBanner } from '../../components/ui/AlertBanner';
import apiClient from '../../config/api.config';
import SaisieRapide from './SaisieRapide';
import SaisieParClasse from './SaisieParClasse';
import SaisieParMatiere from './SaisieParMatiere';
import VisualisationNotes from './VisualisationNotes';
import ActionsRapides from './ActionsRapides';
import OutilsAvances from '../../components/NotesTools/OutilsAvances';
import ScrollToTop from '../../components/ui/ScrollToTop';

type ActiveTab = 'saisie_rapide' | 'saisie_matiere' | 'saisie_liste' | 'visualisation' | 'actions_rapides' | 'outils';

const TABS: { key: ActiveTab; label: string; icon: React.ReactNode; desc: string }[] = [
  {
    key: 'saisie_rapide',
    label: 'Saisie Rapide',
    icon: <ClipboardList size={18} />,
    desc: 'Modes Matriciel, Individuel ou Ligne',
  },
  {
    key: 'saisie_matiere',
    label: 'Saisie par Matière',
    icon: <BookOpen size={18} />,
    desc: 'Verrouille la matière, cherche par nom (tronc commun)',
  },
  {
    key: 'saisie_liste',
    label: 'Saisie par Classe',
    icon: <Table2 size={18} />,
    desc: 'Tableau par classe et matière',
  },
  {
    key: 'visualisation',
    label: 'Visualisation',
    icon: <BarChart3 size={18} />,
    desc: 'Consulter et imprimer les notes',
  },
  {
    key: 'actions_rapides',
    label: 'Actions Rapides',
    icon: <Zap size={18} />,
    desc: 'Reporter examens, devoirs non faits, note collective',
  },
  {
    key: 'outils',
    label: 'Outils Avancés',
    icon: <Wrench size={18} />,
    desc: 'Anomalies, arrondi, plancher/plafond',
  },
];

const SaisieNotesDevoirs: React.FC = () => {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: annees } = useGetAnneesAcademiquesQuery();
  const [activeTab, setActiveTab] = useState<ActiveTab>('saisie_rapide');
  const [anneeAcademique, setAnneeAcademique] = useState('');

  // Année académique de travail : SUIT le sélecteur d'année GLOBAL du top bar
  // (localStorage `annee_academique`, qui pilote aussi le header
  // X-Annee-Academique → schéma PostgreSQL). Fix 2026-10-15 : on ne force plus
  // l'année active au chargement — sinon la saisie des années antérieures était
  // impossible (l'écran restait collé sur l'année active et appliquait ses
  // verrous). On ne réécrit PAS localStorage au unmount : le sélecteur global
  // reste la source de vérité pour toute l'app.
  useEffect(() => {
    const stored = localStorage.getItem('annee_academique');
    if (stored) {
      setAnneeAcademique(stored);
    } else if (anneeActive) {
      setAnneeAcademique(anneeActive.libelle);
    }
  }, [anneeActive]);

  const handleChangeAnnee = (nouvelle: string) => {
    setAnneeAcademique(nouvelle);
    const cible = (annees ?? []).find(a => a.libelle === nouvelle);
    if (nouvelle) {
      localStorage.setItem('annee_academique', nouvelle);
      if (cible?.id) localStorage.setItem('annee_academique_id', String(cible.id));
    }
  };

  const estAnneeActive = !!anneeActive && anneeAcademique === anneeActive.libelle;

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6 py-2">
      {/* En-tête entièrement supprimé (fix 2026-08-27) — l'année est déjà
          visible/sélectionnée globalement via le menu année du top bar, et le
          bouton Recalculer est déplacé plus loin (voir plus bas, dans l'onglet
          Outils Avancés). On garde uniquement le bandeau d'avertissement si
          l'année choisie n'est pas l'année active. */}

      {/* Sélecteur d'année de SAISIE (fix 2026-10-15) — permet de saisir dans
          une année antérieure. Pilote localStorage `annee_academique` (= header
          X-Annee-Academique → schéma). Chaque année a ses propres verrous. */}
      <div className="mb-2 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Année de saisie :</span>
        <select
          value={anneeAcademique}
          onChange={(e) => handleChangeAnnee(e.target.value)}
          className="px-2.5 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500"
        >
          {(annees ?? []).slice().sort((a, b) => b.libelle.localeCompare(a.libelle)).map((a) => (
            <option key={a.id ?? a.libelle} value={a.libelle}>
              {a.libelle}{anneeActive && a.libelle === anneeActive.libelle ? ' (en cours)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Bandeau avertissement quand année ≠ active (unifié — refonte vague 3 lot 2) */}
      {!estAnneeActive && anneeAcademique && (
        <div className="mb-2">
          <AlertBanner
            type="warning"
            message={<><strong>Année archivée — {anneeAcademique}</strong> · les notes iront dans ce schéma.</>}
            action={anneeActive?.libelle ? (
              <button
                onClick={() => handleChangeAnnee(anneeActive.libelle)}
                className="text-[11px] px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white font-medium whitespace-nowrap"
              >
                Revenir à {anneeActive.libelle}
              </button>
            ) : undefined}
          />
        </div>
      )}

      {/* Onglets — barre horizontale compacte (fix 2026-08-27 : ancien 6 cartes
          épaisses avec description → 1 seule ligne icône + label). Un tooltip
          garde la description sur hover. */}
      <div className="mb-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex divide-x divide-gray-200 dark:divide-gray-700 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              title={tab.desc}
              className={`flex-1 min-w-[110px] flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {activeTab === 'saisie_rapide' && (
        <SaisieRapide anneeAcademique={anneeAcademique} />
      )}
      {activeTab === 'saisie_matiere' && (
        <SaisieParMatiere anneeAcademique={anneeAcademique} />
      )}
      {activeTab === 'saisie_liste' && (
        <SaisieParClasse anneeAcademique={anneeAcademique} />
      )}
      {activeTab === 'visualisation' && (
        <VisualisationNotes anneeAcademique={anneeAcademique} />
      )}
      {activeTab === 'actions_rapides' && (
        <ActionsRapides anneeAcademique={anneeAcademique} />
      )}
      {activeTab === 'outils' && (
        <OutilsAvances variante="devoirs" anneeAcademique={anneeAcademique} />
      )}

      <ScrollToTop threshold={500} />
    </div>
  );
};

// Bouton de recalcul rapide pour un niveau (L1/L2/L3)
const RecalculerNiveauButton: React.FC = () => {
  const [niveau, setNiveau] = useState<'L1' | 'L2' | 'L3'>('L1');
  const [recalculing, setRecalculing] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const recalculer = async () => {
    if (recalculing) return;
    setRecalculing(true);
    setMsg(null);
    try {
      const res = await apiClient.post('/portail/admin/recalculer-classe/', { niveau });
      setMsg({ type: 'success', text: `Recalcul lancé pour ${res.data.nb_etudiants || 0} étudiants ${niveau} (en arrière-plan).` });
      setTimeout(() => setMsg(null), 6000);
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.detail || 'Erreur recalcul' });
    } finally {
      setRecalculing(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <select
          value={niveau}
          onChange={(e) => setNiveau(e.target.value as 'L1' | 'L2' | 'L3')}
          className="px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
        >
          <option value="L1">L1</option>
          <option value="L2">L2</option>
          <option value="L3">L3</option>
        </select>
        <button
          onClick={recalculer}
          disabled={recalculing}
          title={`Recalculer toutes les ECUE/UE/Semestre pour ${niveau}. Le système recalcule aussi automatiquement toutes les 30 minutes.`}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded"
        >
          <RefreshCw size={12} className={recalculing ? 'animate-spin' : ''} />
          {recalculing ? 'Recalcul…' : 'Recalculer ' + niveau}
        </button>
      </div>
      {msg && (
        <div className={`text-[10px] px-2 py-1 rounded max-w-xs text-right ${
          msg.type === 'success'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
        }`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

export default SaisieNotesDevoirs;
